import {
  Controller,
  Path,
  PathValue,
  FieldValues,
  useFormContext
} from 'react-hook-form';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { JSX, useEffect } from 'react';
import { CommonFieldProps } from '../../form.types';
import { trpc } from '@client-trpc';

export type SymbolFieldProps<T extends FieldValues> = Omit<
  CommonFieldProps<T>,
  'label'
> & {
  type: 'symbol';
  label?: string;
  exchangeId?: string | null;
};

export const SymbolField = <T extends FieldValues>({
  name,
  label = 'Symbol',
  exchangeId
}: SymbolFieldProps<T>): JSX.Element => {
  const { control, setValue, getValues } = useFormContext<T>();

  const { data: symbols = [], isLoading } = trpc.exchange.symbols.useQuery(
    { exchangeId: exchangeId! },
    {
      enabled: !!exchangeId
    }
  );

  // Clear the input when exchangeId is cleared
  useEffect(() => {
    if (!exchangeId) {
      setValue(name, null as PathValue<T, Path<T>>);
    }
  }, [exchangeId, name, setValue]);

  // Clear the input if the initial value doesn't match any option
  useEffect(() => {
    if (symbols?.length) {
      const currentValue = getValues(name);
      if (currentValue && !symbols.includes(currentValue)) {
        setValue(name, null as PathValue<T, Path<T>>);
      }
    }
  }, [symbols, name, setValue, getValues]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Autocomplete
          {...field}
          options={symbols}
          loading={isLoading}
          onChange={(_, newValue) => {
            setValue(name, newValue as PathValue<T, Path<T>>);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading && <CircularProgress size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
        />
      )}
    />
  );
};
